import {
    Box,
    FormControl,
    FormLabel,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    useMediaQuery
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingSteps from '../../OnboardingSteps/OnboardingSteps';
import { useStateMachine } from 'little-state-machine';
import { updateAction, updateStep } from './updateAction';
import React from 'react';
import { CreateAccountType } from '../../../../utils/hooks/api/account/types';
import { useEffectOnce } from 'usehooks-ts';
import { logger } from '../../../../utils/Helper/logger';

interface IAccountType {
    accountType: CreateAccountType;
}

function AccountType() {
    const isTablet = useMediaQuery('(max-width:768px)');
    const { control, handleSubmit, formState } = useForm<IAccountType>();
    const { isDirty, isValid } = formState;
    const { actions } = useStateMachine({ updateAction, updateStep });
    let navigate = useNavigate();
    const [searchParams] = useSearchParams();
    useEffectOnce(() => {
        localStorage.removeItem('referrer');
        const referrer = searchParams.get('referrer');
        if (referrer) {
            localStorage.setItem('referrer', referrer);
        }
        logger.log('referrer =>', localStorage.getItem('referrer'));
    });
    const onSubmit: SubmitHandler<IAccountType> = async (data) => {
        actions.updateAction(data);
        if (data.accountType === CreateAccountType.Professional) {
            navigate('./professional');
        } else if (data.accountType === CreateAccountType.Company) {
            navigate('./company');
        }
    };
    React.useEffect(() => {
        actions.updateStep(1);
        actions.updateAction();
    }, []);

    return (
        <>
            <Box
                sx={{
                    backgroundColor: { md: 'transparent', sm: '#FCFBF8', xs: 'transparent' },
                    borderRadius: '12px',
                    p: { md: 0, sm: '40px', xs: 0 },
                    mt: { md: '50px', sm: '-290px', xs: 0 },
                    mx: { lg: '50px', md: '20px', sm: '60px', xs: '20px' }
                }}
            >
                <OnboardingSteps />
                <Box
                    sx={{
                        ml: { xl: 10, xs: 'auto' },
                        width: { md: 'fit-content', xs: '100%' },
                        mt: { sm: '71px', xs: '40px' },
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        mx: { sm: 'auto', xs: 0 },
                    }}
                >
                    <FormControl fullWidth>
                        <FormLabel id="account-type">
                            <Typography
                                fontSize={{ sm: '28px', xs: '18px' }}
                                fontWeight={600}
                                sx={{ color: '#23282B' }}
                            >
                                What best describes you?
                            </Typography>
                            {/* checkboxes */}
                        </FormLabel>
                        <Controller
                            rules={{ required: true }}
                            control={control}
                            name="accountType"
                            render={({ field: { onChange } }) => (
                                <RadioGroup
                                    onChange={onChange}
                                    aria-labelledby="account-type"
                                    name="accountType"
                                    sx={{ mt: '32px' }}
                                >
                                    <FormControlLabel
                                        value={CreateAccountType.Professional}
                                        control={
                                            <Radio
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: '#05668D'
                                                    }
                                                }}
                                            />
                                        }
                                        label="I am a Professional"
                                        sx={{
                                            mb: '20px',
                                            borderBottom: '1px solid #EDEDED',
                                            height: '56px',
                                            padding: '16px',
                                            gap: '16px',
                                            alignItems: 'center',
                                            backgroundColor: '#FFFFFF',
                                            color: '#808080',
                                            fontWeight: '400',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            mx: '0'
                                        }}
                                    />
                                    <FormControlLabel
                                        value={CreateAccountType.Company}
                                        control={
                                            <Radio
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: '#05668D'
                                                    }
                                                }}
                                            />
                                        }
                                        label="I am a Company"
                                        sx={{
                                            mb: '48px',
                                            borderBottom: '1px solid #EDEDED',
                                            height: '56px',
                                            padding: '16px',
                                            gap: '16px',
                                            alignItems: 'center',
                                            backgroundColor: '#FFFFFF',
                                            color: '#808080',
                                            fontWeight: '400',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            mx: '0'
                                        }}
                                    />
                                </RadioGroup>
                            )}
                        />
                    </FormControl>
                    <Button
                        variant={'contained'}
                        sx={{
                            backgroundColor: '#05668D',
                            height: '52px',
                            borderRadius: '8px',
                            mb: '24px',
                            textTransform: 'capitalize',
                            fontSize: '16px',
                            fontWeight: '700',
                            lineHeight: '24px',
                            padding: '16px 36px',
                            gap: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={handleSubmit(onSubmit)}
                        disabled={!isDirty || !isValid}
                    >
                        Create Account
                    </Button>
                    <Box
                        sx={{
                            mt: '24px',
                            display: 'flex',
                            height: '36px',
                            width: '272px',
                            alignItems: 'center',
                            flex: 'none',
                            order: '1',
                            flexGrow: '0'
                        }}
                    >
                        <Typography
                            fontSize={'16px'}
                            fontWeight={400}
                            color={'#808080'}
                            sx={{
                                mr: '20px',
                                lineHeight: '24px'
                            }}
                        >
                            Already have an account?
                        </Typography>
                        <Link
                            to={'/login'}
                            style={{
                                padding: '0px, 8px, 0px, 8px',
                                color: '#05668D',
                                fontWeight: '600',
                                fontSize: '14px',
                                lineHeight: ' 20px',
                                width: '46px',
                                height: '20px',
                                textDecoration: 'none'
                            }}
                        >
                            Sign In
                        </Link>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default AccountType;
